import { useState, useMemo, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Pagination, Row, Col, DropdownButton, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAssets, archiveAsset } from '../utils/api';
import Header from './Header';
import Loader from './Loader';
import { faPlus, faPencil, faCar, faFileImport, faFilter, faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import ShowMenuButton from './ShowMenuButton';
import ViewAssetModal from './Modals/ViewAssetModal';
import CreateEditAssetModal from './Modals/CreateEditAssetModal';
import { useDataStore } from '../Store';
import { assetClassToIcon, formatCurrency } from '../utils/helpers';
import Error from './Error';
import ImportAssetModal from './Modals/ImportAssetModal';
import ArchiveAssetModal from './Modals/ArchiveAssetModal';

const Assets = ({ itemSelected, lockedAssetClassFilter, lockedAssetOwnerFilter, lockedAssetOwnerTypeFilter }) => {

    const selectedDealer = useDataStore((state) => state.selectedDealer);
    const selectedEntity = useDataStore((state) => state.selectedEntity);
    const user = useDataStore((state) => state.user);

    const dealer = user?.Entities?.find(c => c.Id === selectedEntity)?.BusinessPartners?.find(c => c.Id == selectedDealer);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [showViewAssetModal, setShowViewAssetModal] = useState(false);
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [showImportAssetModal, setShowImportAssetModal] = useState(false);
    const [showArchiveConfirmationModal, setShowArchiveConfirmationModal] = useState(false);

    const [page, setPage] = useState(1);
    const [assetClassFilter, setAssetClassFilter] = useState(lockedAssetClassFilter || [0]);
    const [assetOwnerFilter, setAssetOwnerFilter] = useState(lockedAssetOwnerFilter || [0]);
    const [assetOwnerTypeFilter, setAssetOwnerTypeFilter] = useState(lockedAssetOwnerTypeFilter || [0]);


    const fetchData = async () => {
        setLoading(true);
        setError(null);
        const data = await getAssets(selectedEntity, page, 30, searchText, assetOwnerFilter, assetClassFilter, assetOwnerTypeFilter);
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };
    useEffect(() => {
        fetchData();
    }, [selectedDealer, searchText, assetClassFilter, assetOwnerFilter, assetOwnerTypeFilter, page]);

    const createAsset = () => {
        setSelectedAsset(null);
        setShowCreateEditModal(true);
    };
    const importAssets = () => {
        setSelectedAsset(null);
        setShowImportAssetModal(true);

    };
    const rowClicked = (asset) => {
        console.log(asset);
        
        if (itemSelected) {
            itemSelected(asset);
            return;
        }
        setSelectedAsset(asset);
        setShowViewAssetModal(true);
    }
    const handleActionClick = (name, asset) => {
        if (name === 'View') {
            setSelectedAsset(asset);
            setShowViewAssetModal(true);
            return;
        }

        if (name === 'Edit') {
            setSelectedAsset(asset);
            setShowCreateEditModal(true);
            return;
        }

        if (name === 'Archive') {
            setSelectedAsset(asset);
            setShowArchiveConfirmationModal(true);
            return;
        }
    }

    const buttons = [
        { id: 0, label: 'Import Assets', variant: 'primary', icon: faFileImport, onClick: () => importAssets() },
        { id: 1, label: 'Create Asset', variant: 'primary', icon: faPlus, onClick: () => createAsset() },
    ];

    const lastPage = data.LastPage;

    return (
        <div style={{ padding: 10 }}>
            {!itemSelected && <Header title='Assets' description="Manage your assets, including creating, deleting, initiating trades, and uploading identifying documents." buttons={buttons} />}
            <Row className="mb-3">
                <Col md={12}>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search assets..."
                            value={searchText}
                            onChange={(e) => {
                                setPage(1);
                                setSearchText(e.target.value)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setPage(1);
                                    fetchData();
                                }
                            }}
                        />
                        <Button variant="outline-secondary" onClick={fetchData}>
                            Search
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {loading ? <Loader /> : error ? <Error /> : (
                <>
                    <Row className="mb-3 mt-3">
                        <Col>
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <Table striped bordered hover style={{ minHeight: 190, minWidth: 600, overflow: 'visible' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                ID
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Class
                                                    <DropdownButton
                                                        id="type-filter"
                                                        title={<FontAwesomeIcon icon={faFilter} />}
                                                        variant={itemSelected ? 'secondary' : 'primary'}
                                                        style={{ height: 24 }}
                                                        className="td-filter-dropdown-button"
                                                        disabled={itemSelected}
                                                        onSelect={(eventKey) => setAssetClassFilter(eventKey)}
                                                    >
                                                        {data?.Filters?.find(c => c.Field == "assetClasses")?.FilterEntries?.map((d, idx) => (
                                                            <Dropdown.Item key={d.Key} eventKey={d.Key} active={assetClassFilter == d.Key}>{d.Text}</Dropdown.Item>
                                                        ))}
                                                    </DropdownButton>
                                                </div>
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Owner
                                                    <DropdownButton
                                                        id="type-filter"
                                                        title={<FontAwesomeIcon icon={faFilter} />}
                                                        variant={itemSelected ? 'secondary' : 'primary'}
                                                        style={{ height: 24 }}
                                                        className="td-filter-dropdown-button"
                                                        disabled={itemSelected}
                                                        onSelect={(eventKey) => setAssetOwnerFilter(eventKey)}
                                                    >
                                                        {data?.Filters?.find(c => c.Field == "assetOwners")?.FilterEntries?.map((d, idx) => (
                                                            <Dropdown.Item key={d.Key} eventKey={d.Key} active={assetOwnerFilter == d.Key}>{d.Text}</Dropdown.Item>
                                                        ))}
                                                    </DropdownButton>
                                                </div>
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Owner Type
                                                    <DropdownButton
                                                        id="owner-type-filter"
                                                        title={<FontAwesomeIcon icon={faFilter} />}
                                                        variant={itemSelected ? 'secondary' : 'primary'}
                                                        style={{ height: 24 }}
                                                        className="td-filter-dropdown-button"
                                                        disabled={itemSelected}
                                                        onSelect={(eventKey) => setAssetOwnerTypeFilter(eventKey)}
                                                    >
                                                        {data?.Filters?.find(c => c.Field == "assetOwnerTypes")?.FilterEntries?.map((d, idx) => (
                                                            <Dropdown.Item key={d.Key} eventKey={d.Key} active={assetOwnerTypeFilter == d.Key}>{d.Text}</Dropdown.Item>
                                                        ))}
                                                    </DropdownButton>
                                                </div>
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Year
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Make
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Model
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Color
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                VIN
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Value
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Credits
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Credit Expiry
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.Results?.map((d, idx) => (
                                            <tr key={idx}>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.AssetClass?.Name}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.AssetOwner?.BusinessPartner?.Name}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.AssetOwner?.BusinessPartner?.BusinessPartnerType?.Description}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Year}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Make}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Model}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Color}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Vin}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{formatCurrency(d?.AdjustedCapitalCost)}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{formatCurrency(d?.Credit)}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.CreditExpiryDate && new Date(d?.CreditExpiryDate).toLocaleDateString()}</td>
                                                {!itemSelected && <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}>{<ShowMenuButton menuItems={[
                                                    { icon: assetClassToIcon(d?.AssetClass?.Id), name: 'View', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faPencil} />, name: 'Edit', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faBoxArchive} />, name: 'Archive', target: d, onClick: handleActionClick },
                                                ]} />}</td>}
                                                {itemSelected && <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}><Button onClick={() => itemSelected(d)}>Select</Button></td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.First disabled={page <= 1} onClick={() => setPage(1)} />
                                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                                <Pagination.Item key={page} active={true}>
                                    {page}
                                </Pagination.Item>
                                <Pagination.Next disabled={page >= lastPage} onClick={() => setPage(page + 1)} />
                                <Pagination.Last disabled={page >= lastPage} onClick={() => setPage(lastPage)} />
                            </Pagination>
                        </Col>
                    </Row>

                    <ViewAssetModal
                        show={showViewAssetModal}
                        onHide={() => setShowViewAssetModal(false)}
                        asset={selectedAsset}
                    />

                    <ImportAssetModal
                        show={showImportAssetModal}
                        onHide={(shouldRefresh) => {
                            setShowImportAssetModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                    />

                    <CreateEditAssetModal
                        show={showCreateEditModal}
                        onHide={(shouldRefresh) => {
                            setShowCreateEditModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                        asset={selectedAsset}
                    />


                    <ArchiveAssetModal
                        show={showArchiveConfirmationModal}
                        onHide={(shouldRefresh) => {
                            setShowArchiveConfirmationModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                        asset={selectedAsset}
                        />

                </>
            )}
        </div>
    );
};

export default Assets;
